import React, { useEffect, useState } from "react";
import Template from "./Template";
import { useActions } from "../redux/actions";
import * as insPlansUcrActions from "../redux/actions/insPlansUcr";
import * as practiceActions from "../redux/actions/practice";
import { Button, Checkbox, Dropdown, Form, Label, Message, Progress, Table } from "semantic-ui-react";

interface UpdateResult {
    practice: string;
    success: boolean;
    updated?: number;
    failed?: number;
    error?: string;
    errors?: { PlanNum: number; error: string }[];
}

function OdInsPlansUcr() {
    const InsPlansUcrActions = useActions(insPlansUcrActions);
    const PracticeActions = useActions(practiceActions);

    const [practiceOpts, setPracticeOpts] = useState<any[]>([]);
    const [selectedPracticeIds, setSelectedPracticeIds] = useState<number[]>([]);
    const [rows, setRows] = useState<any[]>([]);
    const [searchDone, setSearchDone] = useState(false);
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [updating, setUpdating] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
    const [updateResults, setUpdateResults] = useState<UpdateResult[] | null>(null);

    const ALL_PRACTICES = -1;

    useEffect(() => {
        PracticeActions.getAllPractices().then((res: any) => {
            const opts = [
                { key: ALL_PRACTICES, text: "All Practices", value: ALL_PRACTICES },
                ...res.map((item: any) => ({
                    key: item.practiceId,
                    text: item.practice.practice,
                    value: item.practiceId,
                })),
            ];
            setPracticeOpts(opts);
        });
    }, []);

    const handlePracticeChange = (value: number[]) => {
        const justAddedAll = value.includes(ALL_PRACTICES) && !selectedPracticeIds.includes(ALL_PRACTICES);
        if (justAddedAll) {
            setSelectedPracticeIds([ALL_PRACTICES]);
        } else {
            setSelectedPracticeIds(value.filter((v) => v !== ALL_PRACTICES));
        }
    };

    const resolvePracticeIds = () => {
        const realIds = practiceOpts.filter((o: any) => o.value !== ALL_PRACTICES).map((o: any) => o.value);
        if (!selectedPracticeIds.length || selectedPracticeIds.includes(ALL_PRACTICES)) return realIds;
        return selectedPracticeIds;
    };

    const loadCounts = (ids: number[]) =>
        InsPlansUcrActions.getOdInsPlansUcr(ids).then((res: any) => {
            const data = Array.isArray(res) ? res : [];
            setRows(data.sort((a: any, b: any) => (a.practice || "").localeCompare(b.practice || "")));
            setCheckedIds([]);
            setSearchDone(true);
        });

    const handleSearch = () => {
        setUpdateResults(null);
        loadCounts(resolvePracticeIds());
    };

    // Only practices that loaded and still have plans to fix can be selected
    const selectableIds = rows.filter((r: any) => r.success && r.notUsingUcr > 0).map((r: any) => r.practiceId);
    const allChecked = selectableIds.length > 0 && selectableIds.every((id: number) => checkedIds.includes(id));
    const totalNotUsingUcr = rows.reduce((sum: number, r: any) => sum + (r.success ? r.notUsingUcr : 0), 0);
    const selectedPlanCount = rows
        .filter((r: any) => checkedIds.includes(r.practiceId))
        .reduce((sum: number, r: any) => sum + r.notUsingUcr, 0);

    const toggleAll = (checked: boolean) => setCheckedIds(checked ? selectableIds : []);

    const toggleOne = (practiceId: number, checked: boolean) =>
        setCheckedIds((prev) => checked ? [...prev, practiceId] : prev.filter((id) => id !== practiceId));

    const handleUpdate = async () => {
        const targets = rows.filter((r: any) => checkedIds.includes(r.practiceId));
        if (!targets.length) return;
        const ok = window.confirm(
            `Set ClaimsUseUCR = true and uncheck "PPO substitution calculate writeoffs" on ${selectedPlanCount} insurance plan(s) across ${targets.length} practice(s)?\n\n` +
            `This changes the plan itself, so every subscriber/patient on those plans is affected.`
        );
        if (!ok) return;

        setUpdating(true);
        setUpdateResults(null);
        const results: UpdateResult[] = [];
        for (let i = 0; i < targets.length; i++) {
            setProgress({ done: i, total: targets.length, current: targets[i].practice });
            const res: any = await InsPlansUcrActions.updateOdInsPlansUcr(targets[i].practiceId);
            results.push({ ...res, practice: (res && res.practice) || targets[i].practice });
        }
        setProgress({ done: targets.length, total: targets.length, current: "" });
        setUpdateResults(results);
        setUpdating(false);
        loadCounts(rows.map((r: any) => r.practiceId));
    };

    const describeResult = (r: UpdateResult) => {
        if (r.error) return r.error;
        const parts = [`${r.updated || 0} updated`];
        if (r.failed) parts.push(`${r.failed} failed`);
        return parts.join(", ");
    };

    return (
        <Template activeLink="ins-plans-ucr">
            <div className="ui card">
                <div className="content">
                    <h2>Ins Plans - Claims Use UCR</h2>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Practices</label>
                                <Dropdown
                                    placeholder="All Practices"
                                    options={practiceOpts}
                                    selection={true}
                                    search={true}
                                    multiple={true}
                                    value={selectedPracticeIds}
                                    onChange={(_e, { value }) => handlePracticeChange(value as number[])}
                                />
                            </Form.Field>
                            <Form.Field style={{ display: "flex", alignItems: "flex-end" }}>
                                <Button primary onClick={handleSearch} disabled={updating}>Search</Button>
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </div>
            </div>

            {updating && (
                <div className="ui card">
                    <div className="content">
                        <Progress
                            value={progress.done}
                            total={progress.total}
                            progress="ratio"
                            indicating={true}
                            label={progress.current ? `Updating ${progress.current}...` : "Finishing..."}
                        />
                    </div>
                </div>
            )}

            {updateResults && (
                <Message
                    onDismiss={() => setUpdateResults(null)}
                    positive={updateResults.every(r => r.success)}
                    negative={updateResults.some(r => !r.success)}
                >
                    <Message.Header>
                        {updateResults.filter(r => r.success).length} practice(s) succeeded, {updateResults.filter(r => !r.success).length} with issues
                    </Message.Header>
                    <Message.List>
                        {updateResults.map((r, i) => (
                            <Message.Item key={i}>
                                <strong>{r.practice}</strong>: {describeResult(r)}
                                {r.errors && r.errors.length > 0 && (
                                    <Message.List>
                                        {r.errors.map((e) => (
                                            <Message.Item key={e.PlanNum}>PlanNum {e.PlanNum}: {e.error}</Message.Item>
                                        ))}
                                    </Message.List>
                                )}
                            </Message.Item>
                        ))}
                    </Message.List>
                </Message>
            )}

            {searchDone && (
                <div className="ui card">
                    <div className="content">
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                            <div>
                                <h3 style={{ margin: 0 }}>
                                    Results ({rows.length} practices, {totalNotUsingUcr} plans with ClaimsUseUCR = false)
                                </h3>
                                <small>Counts come from the replica DB and may take a few minutes to reflect updates.</small>
                            </div>
                            <Button
                                positive
                                disabled={!checkedIds.length || updating}
                                onClick={handleUpdate}
                            >
                                Set ClaimsUseUCR = true ({checkedIds.length} practice(s), {selectedPlanCount} plans)
                            </Button>
                        </div>
                        <Table celled={true}>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell collapsing>
                                        <Checkbox
                                            checked={allChecked}
                                            disabled={!selectableIds.length || updating}
                                            onChange={(_e, { checked }) => toggleAll(!!checked)}
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Practice</Table.HeaderCell>
                                    <Table.HeaderCell>Total Plans</Table.HeaderCell>
                                    <Table.HeaderCell>ClaimsUseUCR = false</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {rows.map((r: any) => (
                                    <Table.Row key={r.practiceId} negative={!r.success}>
                                        <Table.Cell collapsing>
                                            <Checkbox
                                                checked={checkedIds.includes(r.practiceId)}
                                                disabled={!selectableIds.includes(r.practiceId) || updating}
                                                onChange={(_e, { checked }) => toggleOne(r.practiceId, !!checked)}
                                            />
                                        </Table.Cell>
                                        <Table.Cell><Label>{r.practice}</Label></Table.Cell>
                                        {r.success ? (
                                            <>
                                                <Table.Cell warning={r.total === 0}>
                                                    {r.total === 0 ? "0 (no data - DB may be unreachable)" : r.total}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {r.notUsingUcr > 0 ? <Label color="orange">{r.notUsingUcr}</Label> : 0}
                                                </Table.Cell>
                                            </>
                                        ) : (
                                            <Table.Cell colSpan={2}>Error: {String(r.error)}</Table.Cell>
                                        )}
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </div>
            )}
        </Template>
    );
}

export default OdInsPlansUcr;
